import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl
} from '@mui/material';
import dayjs from 'dayjs';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

interface EmailLog {
  _id: string;
  email: string;
  domain: string;
  status: string;
  timestamp: string;
  subject?: string;
}

// Map raw status values to user-friendly display names
const statusMap: Record<string, string> = {
  'request': 'Sent',
  'opened': 'Opened',
  'click': 'Link clicked',
  'unique_opened': 'First open',
  'delivered': 'Delivered',
  'bounced': 'Bounced',
  'spam': 'Marked as spam',
  'blocked': 'Blocked',
  'queued': 'Queued',
  'sent': 'Sent'
};

// Status options for filter dropdown
const statusOptions = [
  '', 'delivered', 'opened', 'bounced', 'spam', 'blocked', 'queued', 'sent', 'click', 'unique_opened', 'request'
];

const EmailLogs: React.FC = () => {
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    status: '',
    email: '',
    domain: '',
    from: '',
    to: ''
  });
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);

  const fetchLogs = async () => {
    setLoading(true);
    const params = new URLSearchParams({
      ...filters,
      page: (page + 1).toString(),
      limit: rowsPerPage.toString()
    });
    const res = await fetch(`/api/email-logs?${params.toString()}`);
    const data = await res.json();
    setLogs(data.logs);
    setTotal(data.total);
    setLoading(false);
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line
  }, [page, rowsPerPage]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name as string]: value }));
  };

  const handleStatusChange = (e: any) => {
    setFilters(prev => ({ ...prev, status: e.target.value }));
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    setPage(0);
    fetchLogs();
  };

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>Email Logs</Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            label="Recipient Email"
            name="email"
            size="small"
            value={filters.email}
            onChange={handleFilterChange}
          />
          <TextField
            label="Domain"
            name="domain"
            size="small"
            value={filters.domain}
            onChange={handleFilterChange}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              name="status"
              value={filters.status}
              onChange={handleStatusChange}
            >
              {statusOptions.map(opt => (
                <MenuItem key={opt} value={opt}>{opt ? statusMap[opt] || opt : 'Any'}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="From"
            name="from"
            type="date"
            size="small"
            value={filters.from}
            onChange={handleDateChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            name="to"
            type="date"
            size="small"
            value={filters.to}
            onChange={handleDateChange}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" onClick={handleSearch} disabled={loading} sx={{ minWidth: 100 }}>Search</Button>
        </Box>
      </Paper>
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date/Time</TableCell>
                <TableCell>Domain</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map(log => (
                <TableRow key={log._id}>
                  <TableCell>{log.email}</TableCell>
                  <TableCell>{log.subject || '-'}</TableCell>
                  <TableCell>{statusMap[log.status] || log.status}</TableCell>
                  <TableCell>{dayjs(log.timestamp).format('YYYY-MM-DD HH:mm')}</TableCell>
                  <TableCell>{log.domain}</TableCell>
                  <TableCell>
                    <Button variant="outlined" size="small" onClick={() => setSelectedLog(log)}>
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center">No logs found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    {/* View Details Dialog */}
    {selectedLog && (
      <Dialog open={!!selectedLog} onClose={() => setSelectedLog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Email Log Details</DialogTitle>
        <DialogContent dividers>
          <Box mb={2}>
            <Typography variant="subtitle2">Email:</Typography>
            <Typography>{selectedLog.email}</Typography>
          </Box>
          <Box mb={2}>
            <Typography variant="subtitle2">Subject:</Typography>
            <Typography>{selectedLog.subject || '-'}</Typography>
          </Box>
          <Box mb={2}>
            <Typography variant="subtitle2">Status:</Typography>
            <Typography>{statusMap[selectedLog.status] || selectedLog.status}</Typography>
          </Box>
          <Box mb={2}>
            <Typography variant="subtitle2">Domain:</Typography>
            <Typography>{selectedLog.domain}</Typography>
          </Box>
          <Box mb={2}>
            <Typography variant="subtitle2">Date/Time:</Typography>
            <Typography>{dayjs(selectedLog.timestamp).format('YYYY-MM-DD HH:mm')}</Typography>
          </Box>
          {/* Optionally show raw JSON */}
          {'raw' in selectedLog && (
            <Box mb={2}>
              <Typography variant="subtitle2">Raw Event Data:</Typography>
              <pre style={{ background: '#f7f7f7', padding: 8, borderRadius: 4, maxHeight: 300, overflow: 'auto' }}>
                {JSON.stringify((selectedLog as any).raw, null, 2)}
              </pre>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedLog(null)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    )}
  </Box>
  );
};

export default EmailLogs;
